import {
  Box,
  Button,
  Flex,
  Link,
  Typography,
  useDrawerContext,
} from '@iheartjane/reefer';
import { useRuntimeConfig } from '@iheartjane/runtime-config';
import { formatCurrency } from '@iheartjane/utils';
import find from 'lodash-es/find';
import isEmpty from 'lodash-es/isEmpty';
import { matchRoutes, useLocation, useNavigate, useParams } from 'react-router';

import { useQuery } from '@jane/shared-ecomm/hooks';
import { useGetStore } from '@jane/shared/data-access';
/* eslint-disable-next-line no-restricted-imports */
import type { StoreSpecial } from '@jane/shared/models';
import { useEcommApp } from '@jane/shared/providers';
import type { DeliveryValidation } from '@jane/shared/types';
import { getEcommPaths, postMessageToIframeParent } from '@jane/shared/util';

import { useBrandPartnerContext } from '../../../../common/providers/brandPartnerProvider';
import { useCustomerDispatch } from '../../../../customer/dispatch';
import {
  closeCart,
  fixUnavailableCheckout,
  refreshCart,
  selectCart,
} from '../../../../customer/redux/cart';
import { selectOperatorEmbedded } from '../../../../customer/redux/operatorEmbedded';
import { useCustomerSelector } from '../../../../customer/selectors';
import { CartSource } from '../../../../sources/cart';
import { DrawerFooter } from './cartDrawerFooter.styles';

interface CartDrawerFooterProps {
  /**
   * Internal flag to configure headless styles to be initially tested in Bloom
   */
  bloomUserExperience?: boolean;
  checkoutButtonLabel?: string;
  checkoutDisabled: boolean;
  deliveryValidationData: DeliveryValidation;
  handleSubmit: () => void;
  headless?: boolean;
  headlessGuestCheckoutDisabled?: boolean;
  headlessLoginUrl?: string;
  specials: StoreSpecial[];
  total?: number;
}

export const CartDrawerFooter = ({
  bloomUserExperience = false,
  checkoutButtonLabel = 'Proceed to Checkout',
  checkoutDisabled,
  handleSubmit,
  headless = false,
  headlessGuestCheckoutDisabled = false,
  headlessLoginUrl = '',
  total,
  specials,
}: CartDrawerFooterProps) => {
  const location = useLocation();
  const { onRequestClose } = useDrawerContext();
  const navigate = useNavigate();
  const dispatch = useCustomerDispatch();
  const query = useQuery();
  const { special_id } = useParams();
  const { appMode } = useEcommApp();
  const brandPartner = useBrandPartnerContext();
  const { isBoostForAll, isPartnerHosted, partnerHostedPath } =
    useRuntimeConfig();
  const { operatorId } = useCustomerSelector(selectOperatorEmbedded);
  const { cart } = useCustomerSelector(selectCart);
  const { data: store } = useGetStore(cart?.store?.id);

  const onSubmit = async () => {
    const { unavailable_product_ids } =
      await CartSource.getUnavailableProductIds(cart.uuid);
    if (checkoutDisabled) return;

    if (isEmpty(unavailable_product_ids)) {
      handleSubmit();
    } else {
      dispatch(fixUnavailableCheckout());
      dispatch(refreshCart(cart));
    }
  };

  const ecommPaths = getEcommPaths(
    {
      appMode,
      brandId: brandPartner?.id,
      isBoostForAll,
      isPartnerHosted,
      operatorId,
      partnerHostedPath,
      storePathProps: { id: store?.id ?? '', name: store?.name ?? '' },
    },
    location.search
  );

  const getContinueShoppingUrl = () => {
    const { fromSpecialId } = query;
    const fromSpecial = find(specials, {
      id: parseInt(special_id!) || parseInt(fromSpecialId),
    });

    const storeUrl = ecommPaths.menu();

    if (appMode === 'default') {
      return fromSpecial
        ? ecommPaths.menuSpecial({
            id: fromSpecial?.id,
            title: fromSpecial?.title,
          })
        : storeUrl;
    }

    return ecommPaths.menu();
  };

  const handleContinueShopping = () => {
    const path = getContinueShoppingUrl();
    // If the url is the same or we're already looking at a menu
    const noNavigationRequired =
      matchRoutes([{ path }], location) ||
      (location.pathname.includes('/menu') &&
        !location.pathname.includes('/products'));

    if (headless) {
      onRequestClose();
      postMessageToIframeParent({
        messageType: 'analyticsEvent',
        payload: {
          name: 'drawerContinueShopping',
        },
      });
      return;
    }

    // if we're already on the menu/specials page, just request the close (which includes the animation),
    if (noNavigationRequired) {
      onRequestClose();
      return;
    }

    // otherwise immediately close the drawer and navigate.
    dispatch(closeCart());
    navigate(path);
  };

  return (
    <DrawerFooter
      fixedFooter={bloomUserExperience ? false : headless}
      noPadding
    >
      <Box background="grays-white" px={24} pb={24}>
        <Flex flexDirection="column">
          <Flex mt={16} justifyContent="space-between">
            <Typography branded variant="header-bold" as="p">
              Estimated Total
            </Typography>
            <Typography
              branded
              variant="header-bold"
              as="p"
              data-testid="estimated-total"
            >
              {formatCurrency(total)}
            </Typography>
          </Flex>
          <Flex flexDirection="column" alignItems="center">
            {headlessGuestCheckoutDisabled && headlessLoginUrl ? (
              <Button
                full
                href={headlessLoginUrl}
                target="_parent"
                label="Sign in to checkout"
                my={16}
              ></Button>
            ) : (
              <Button
                onClick={onSubmit}
                label={checkoutButtonLabel}
                full
                disabled={checkoutDisabled}
                my={16}
              />
            )}
            <Link typography="body" onClick={handleContinueShopping}>
              {bloomUserExperience
                ? 'Keep shopping this store'
                : 'Continue shopping'}
            </Link>
          </Flex>
        </Flex>
      </Box>
    </DrawerFooter>
  );
};
